import React from "react";
import styled from "styled-components";
import { useMutation, queryCache } from "react-query";
import { Button, Alert, Radio, Row, Col, Popconfirm, Empty } from "antd";
import { EyeOutlined, DeleteOutlined, EditOutlined } from "@ant-design/icons";

import CustomLink from "components/shared/CustomLink";
import * as ROUTES from "constants/routes";
import { apiAxios } from "services/HttpServices";
import { NotificationStateContext } from "context/UI/Notification/NotificationState";
import useTodoTemplateData from "hooks/api/todo/template/useTodoTemplateData";
import Link from "components/shared/CustomLink";
import { TextInputSkeleton } from "lib/loader/shared/textInputSkeleton";

const deleteTemplate = async ({ todoTemplateId }) => {
  try {
    const res = await apiAxios.delete(`/todos/task-template/${todoTemplateId}`);
    return res.status;
  } catch (e) {
    throw e.response.data.message;
  }
};

const TodoTemplateList = (props) => {
  const { status, data } = useTodoTemplateData();
  const [error, setError] = React.useState(null);
  const [showIndexDetail, setShowIndexDetail] = React.useState(null);
  // Global State
  const {
    state: notificationState,
    setSuccessMessage,
    clearSuccessMessage,
    setLoading,
    finishLoading,
  } = React.useContext(NotificationStateContext);

  const [mutateDelete] = useMutation(deleteTemplate, {
    onSuccess: (_, variables) => {
      queryCache.setQueryData(["todo-template"], function (oldData) {
        const newData = oldData.filter(
          (data) => data.id !== variables.todoTemplateId
        );
        return newData;
      });
    },
    onError: (e) => {
      setError(e);
    },
  });

  const showTaskDetail = (todoTemplateId) => {
    setShowIndexDetail(todoTemplateId);
  };

  const cancelDeleteTodoTemplate = (e) => {
    console.log("e :>> ", e.target.value);
  };

  const deleteTodoTemplate = async (todoTemplateId) => {
    setLoading(`deleteTodoTemplate-${todoTemplateId}`);
    try {
      await mutateDelete({ todoTemplateId });
      finishLoading(`deleteTodoTemplate-${todoTemplateId}`);
      setSuccessMessage("Succesfully deleted todo template");
      clearSuccessMessage();
    } catch (error) {
      finishLoading(`deleteTodoTemplate-${todoTemplateId}`);
      console.log(error);
    }
  };

  if (
    (status !== "loading" || status !== "idle") &&
    status === "success" &&
    data.length === 0
  ) {
    return (
      <EmptyContainer>
        <Empty
          image="https://gw.alipayobjects.com/zos/antfincdn/ZHrcdLPrvN/empty.svg"
          imageStyle={{
            height: 60,
          }}
          description={<span>No todo template</span>}
        >
          <CustomLink href={ROUTES.TODO_TEMPLATE_ADD}>
            <Button type="primary">Create Now</Button>
          </CustomLink>
        </Empty>
      </EmptyContainer>
    );
  }

  return (
    <>
      <Row>
        {status === "loading" ? (
          <>
            <Col xs={24}>
              <ListContainer>
                <StyledList>
                  <TextInputSkeleton />
                </StyledList>
              </ListContainer>
            </Col>
            <Col xs={24}>
              <ListContainer>
                <StyledList>
                  <TextInputSkeleton />
                </StyledList>
              </ListContainer>
            </Col>
            <Col xs={24}>
              <ListContainer>
                <StyledList>
                  <TextInputSkeleton />
                </StyledList>
              </ListContainer>
            </Col>
          </>
        ) : (
          <>
            {data.map((todoTemplate) => (
              <Col xs={24} key={todoTemplate.id}>
                <ListContainer>
                  <StyledList>
                    <div className="left-content">
                      <h1>{todoTemplate.title}</h1>
                      <div className="extra">
                        <p className="code">
                          {todoTemplate.code.toUpperCase()}
                        </p>
                      </div>
                    </div>
                    <div className="right-content">
                      <div className="task">
                        <p>TASK COUNT</p>
                        <h2>{todoTemplate.taskCount}</h2>
                      </div>
                      <div className="action">
                        <div className="label">
                          <p>ACTIONS</p>
                        </div>
                        <div className="button">
                          <div className="button-container">
                            <Button
                              type="primary"
                              icon={<EyeOutlined />}
                              size="medium"
                              onClick={() => showTaskDetail(todoTemplate.id)}
                            />
                          </div>
                          {props.selectable ? (
                            <div className="button-container">
                              <Button
                                size="medium"
                                onClick={() =>
                                  props.handleSelectTodoTemplate(
                                    todoTemplate.id
                                  )
                                }
                              >
                                SELECT
                              </Button>
                            </div>
                          ) : (
                            <>
                              <Link
                                href={`/todo/template/[id]/update`}
                                as={`/todo/template/${todoTemplate.id}/update`}
                              >
                                <div className="button-container">
                                  <Button
                                    icon={<EditOutlined />}
                                    size="medium"
                                  />
                                </div>
                              </Link>
                              <div className="button-container">
                                <Popconfirm
                                  title="Are you sure delete this todo template?"
                                  onConfirm={() =>
                                    deleteTodoTemplate(todoTemplate.id)
                                  }
                                  onCancel={cancelDeleteTodoTemplate}
                                  okText="Yes"
                                  cancelText="No"
                                >
                                  <Button
                                    type="primary"
                                    danger
                                    icon={<DeleteOutlined />}
                                    size="medium"
                                    loading={
                                      notificationState.loading[
                                        `deleteTodoTemplate-${todoTemplate.id}`
                                      ]
                                    }
                                  />
                                </Popconfirm>
                              </div>
                            </>
                          )}
                        </div>
                      </div>
                    </div>
                  </StyledList>
                  {showIndexDetail === todoTemplate.id && (
                    <TaskContainer>
                      {todoTemplate.template.map((task, i) => (
                        <div className="task-row" key={`task-${i}`}>
                          <div className="left-content">
                            <p className="title">{task.title}</p>
                            <p className="description">{task.desc}</p>
                          </div>
                          {task.due_after && (
                            <div className="right-content">
                              <p className="detail">{task.due_after}</p>
                              <span className="unit">Days</span>
                            </div>
                          )}
                        </div>
                      ))}
                    </TaskContainer>
                  )}
                </ListContainer>
              </Col>
            ))}
          </>
        )}
      </Row>
    </>
  );
};

const TaskContainer = styled.div`
  margin-top: ${(props) => props.theme.spacing[2]};
  border: 1px solid ${(props) => props.theme.colors.grey[300]};
  background-color: ${(props) => props.theme.colors.grey[100]};
  padding: ${(props) => props.theme.spacing[2]};

  .task-row {
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding-bottom: ${(props) => props.theme.spacing[2]};
    margin-bottom: ${(props) => props.theme.spacing[2]};
    border-bottom: 1px solid ${(props) => props.theme.colors.grey[300]};

    .left-content {
      display: flex;
      flex-direction: column;

      .title {
        font-size: ${(props) => props.theme.fontSize.l};
      }

      .description {
        font-size: ${(props) => props.theme.fontSize.xs};
        color: ${(props) => props.theme.colors.grey[600]};
      }
    }

    .right-content {
      display: flex;
      flex-direction: column;
      align-items: center;
      .label {
        font-size: ${(props) => props.theme.fontSize.xs};
        text-transform: uppercase;
        color: ${(props) => props.theme.colors.grey[500]};
      }

      .detail {
        font-size: ${(props) => props.theme.fontSize.m};
      }

      .unit {
        font-size: ${(props) => props.theme.fontSize.xs};
        text-transform: uppercase;
        color: ${(props) => props.theme.colors.grey[500]};
      }
    }

    :last-child {
      border-bottom: none;
      margin-bottom: 0;
      padding-bottom: 0;
    }

    p {
      margin-bottom: 0;
    }
  }
`;

const StyledList = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;

  h1 {
    margin-bottom: 0;
    font-size: ${(props) => props.theme.fontSize["base"]};
  }

  .left-content {
    display: flex;
    flex-direction: column;

    .extra {
      margin-top: ${(props) => props.theme.spacing[1]};
      display: flex;
      p {
        color: ${(props) => props.theme.colors.grey[600]};
        margin-bottom: 0;
      }

      .code {
        margin-right: ${(props) => props.theme.spacing[2]};
      }
    }
  }

  .right-content {
    display: flex;
    align-items: flex-start;

    p {
      margin-bottom: 0;
      font-size: ${(props) => props.theme.fontSize.xs};
      color: ${(props) => props.theme.colors.grey[500]};
    }

    h2 {
      margin-bottom: 0;
    }

    .task {
      display: flex;
      flex-direction: column;
      align-items: center;
      justify-content: center;
    }

    .action {
      display: flex;
      flex-direction: column;
      margin-left: ${(props) => props.theme.spacing[2]};

      .label {
        align-self: center;
        margin-bottom: ${(props) => props.theme.spacing[2]};
      }

      .button {
        display: flex;

        .button-container {
          margin-left: ${(props) => props.theme.spacing[1]};
        }
      }
    }
  }
`;

const ListContainer = styled.div`
  background: ${(props) => props.theme.colors.white};
  padding: 12px ${(props) => props.theme.spacing[4]};

  border: 1px solid ${(props) => props.theme.colors.grey[400]};
  margin-bottom: ${(props) => props.theme.spacing[2]};
`;

const EmptyContainer = styled.div`
  height: 30%;
  padding: ${(props) => props.theme.spacing[2]};
  background-color: ${(props) => props.theme.colors.white};
  border: 1px solid ${(props) => props.theme.colors.grey[400]};
  display: flex;
  align-items: center;
  justify-content: center;
`;

export default TodoTemplateList;
